import { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';

export type ModelServingStatus =
  | 'healthy'
  | 'degraded'
  | 'unhealthy'
  | 'idle'
  | 'not_configured';

export interface ModelServingHealthEntry {
  name: string;
  group: string;
  version: string;
  pool: string;
  configured: boolean;
  status: ModelServingStatus;
  invocations: number;
  served: number;
  fell_back: number;
  served_rate: number | null;
  cloud_attempts: number;
  cloud_errors: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
}

export interface ModelServingHealth {
  generated_at: string;
  window_hours: number;
  database_available: boolean;
  models: ModelServingHealthEntry[];
}

const POLL_INTERVAL_MS = 60_000;

/**
 * Passive self-served model health: the backend summarises recent real calls,
 * so polling this never wakes a scaled-to-zero pool.
 */
export function useModelServingHealth(enabled = true): ModelServingHealth | null {
  const [health, setHealth] = useState<ModelServingHealth | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await apiFetch('/api/model-serving/health');
        if (!response.ok) return;
        const data = (await response.json()) as ModelServingHealth;
        if (!cancelled) setHealth(data);
      } catch {
        // Keep the last snapshot; the panel simply stops updating.
      }
    };
    void load();
    const timer = window.setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled]);

  return health;
}
